/**
 * Keetsa size/depth variant matching — shared resolver.
 *
 * Extracted from `snippets/product-addon-checkbox.liquid` (KEET-532) so the
 * bundle PDP (KEET-559) can reuse the same matching rules instead of
 * reimplementing them. Behavior is intentionally identical to the inline
 * version it replaced — see the KEET-532 test matrix.
 *
 * Consumers: snippets/product-addon-checkbox.liquid
 *
 * All functions are pure. Nothing here touches the DOM, subscribes to events,
 * or holds state, so a caller can drive a checkbox, a <select>, or anything else.
 *
 * Loaded as a plain deferred script (no build step in this repo), exposed on
 * `window.KeetsaSizeMatch`. Deferred scripts finish before DOMContentLoaded, so
 * callers that initialise on DOMContentLoaded can rely on it being present —
 * but callers should still guard, because a failed asset load must degrade to
 * "no match" rather than leaving a control in an unsafe state.
 */
window.KeetsaSizeMatch = (function () {
  'use strict';

  // KEET-532 — business rule (confirm with merchandising before changing):
  // mattresses up to this height (inches) take the 12"H protector; taller take 14"H.
  var DEFAULT_DEPTH_THRESHOLD_IN = 12;

  /**
   * Strips dimension info and normalizes spacing/hyphens so that
   * "King-12\"H ( 76\"W x 80\"L x 12\"H)" → "king"
   * "Cal King" = "CalKing" = "Cal-King" → "calking"
   * "California King" → "calking"
   * "King" never matches "Cal King" (exact match only, no substring)
   */
  function normalizeSizeName(str) {
    if (!str) return '';
    return str
      .toLowerCase()
      .replace(/\s*\([^)]*\)/g, '')          // strip (76"W x 80"L x 12"H)
      .replace(/-?\s*\d+\s*["']?\s*[hwl]\b/gi, '') // strip -12"H dimension suffixes
      .replace(/\d+["']/g, '')               // strip remaining inch/foot notations
      .replace(/\bcalifornia\b/g, 'cal')     // "California King" → "cal king"
      .replace(/[^a-z0-9]/g, '');            // remove spaces, hyphens, quotes → "calking"
  }

  /**
   * KEET-532 — depth token, read from the pre-parenthetical part only:
   * "Queen-12\"H ( 60\"W x 80\"L x 12\"H)" → 12   (protector pocket depth)
   * "Twin (38\"W x 75\"L x 14\"H)"         → null (bed-frame height sits inside
   *                                                the parens — not a pocket depth)
   * "Queen"                                → null (single-depth products)
   */
  function extractDepthInches(str) {
    if (!str) return null;
    var prefix = str.replace(/\([^)]*\)/g, ' ');
    var match = prefix.match(/(\d+(?:\.\d+)?)\s*["”′']?\s*h\b/i);
    return match ? parseFloat(match[1]) : null;
  }

  /** Depth for a variant, reading option1/2/3 in order. Cached on `_depthIn` when present. */
  function variantDepthInches(variant) {
    if (!variant) return null;
    if (variant._depthIn !== undefined) return variant._depthIn;
    var depth = extractDepthInches(variant.option1);
    if (depth === null) depth = extractDepthInches(variant.option2);
    if (depth === null) depth = extractDepthInches(variant.option3);
    return depth;
  }

  /**
   * Single-variant products ("Default Title") are universal — they have no size
   * to match and must keep working exactly as before KEET-532.
   */
  function isSingleUniversalAddon(variants) {
    if (!Array.isArray(variants) || variants.length !== 1) return false;
    return normalizeSizeName(variants[0].option1) === 'defaulttitle';
  }

  /**
   * Maps a mattress height (inches) to the protector depth it needs.
   * Returns null when the height is absent or unparseable — callers treat that
   * as "size-only matching", which is the pre-KEET-532 behavior.
   */
  function requiredDepthFor(heightIn, thresholdIn) {
    var height = parseFloat(heightIn);
    if (Number.isNaN(height)) return null;
    var threshold = typeof thresholdIn === 'number' ? thresholdIn : DEFAULT_DEPTH_THRESHOLD_IN;
    return height <= threshold ? 12 : 14;
  }

  /**
   * Resolve the variant matching a set of selected option values.
   *
   * @param {Array} variants - candidate variants: { option1, option2, option3, available }
   * @param {Array} selectedOptionValues - the driving product's option values
   * @param {Object} [options]
   * @param {number|null} [options.requiredDepthIn] - null skips the depth pass
   * @param {boolean} [options.allowDeeperDepthFallback=false] - offer next-deeper depth
   *        when the exact depth has no available variant, instead of returning null
   * @param {string} [options.requiredStyle] - style word the candidate must carry, e.g.
   *        "Traditional". Absent => no style filtering (mattress behaviour).
   * @param {boolean} [options.singleUniversal] - override the universal-addon detection
   * @returns {Object|null} the matched variant, or null when nothing is safe to offer
   */
  function findMatchingVariant(variants, selectedOptionValues, options) {
    if (!Array.isArray(variants) || variants.length === 0) return null;

    var opts = options || {};
    var requiredDepthIn = opts.requiredDepthIn === undefined ? null : opts.requiredDepthIn;
    var allowDeeperDepthFallback = opts.allowDeeperDepthFallback === true;

    // KEET-569 — optional style filter.
    //
    // The Pillow Protector's option is a Style ("Traditional Standard", "Contour Small"),
    // while a pillow only offers a size ("Standard"). The exact-equality scoring below can
    // never match those, so a caller that knows its product's style passes it here: a
    // candidate must carry that style, and only the remainder is compared against the
    // selected size. Absent => inert, so mattress matching is unchanged.
    //
    // Deliberately not substring matching. Treating "standard" as matching anything ending
    // in "standard" would silently pick between "Contour Standard" and "Traditional
    // Standard" — a wrong-style protector is as bad as a wrong-size one.
    var stylePrefix = normalizeSizeName(opts.requiredStyle || '');
    var singleUniversal =
      opts.singleUniversal === undefined ? isSingleUniversalAddon(variants) : opts.singleUniversal;

    if (singleUniversal) {
      return variants[0].available ? variants[0] : null;
    }

    var mainOpts = (selectedOptionValues || [])
      .filter(Boolean)
      .map(normalizeSizeName)
      .filter(Boolean);

    // Pass 1 — size candidates among available variants.
    var candidates = [];
    var bestScore = 0;
    for (var i = 0; i < variants.length; i++) {
      var av = variants[i];
      if (!av.available) continue;

      var addonOpts = [av.option1, av.option2, av.option3]
        .filter(Boolean)
        .map(normalizeSizeName)
        .filter(Boolean);

      if (stylePrefix) {
        var carriesStyle = false;
        for (var s = 0; s < addonOpts.length; s++) {
          if (addonOpts[s].indexOf(stylePrefix) === 0 && addonOpts[s].length > stylePrefix.length) {
            addonOpts[s] = addonOpts[s].slice(stylePrefix.length);
            carriesStyle = true;
          }
        }
        if (!carriesStyle) continue;
      }

      var score = 0;
      for (var m = 0; m < mainOpts.length; m++) {
        for (var a = 0; a < addonOpts.length; a++) {
          if (mainOpts[m] === addonOpts[a] && mainOpts[m] !== '') {
            score++;
          }
        }
      }

      if (score > 0) {
        candidates.push({ variant: av, score: score });
        if (score > bestScore) bestScore = score;
      }
    }

    // KEET-532 — no size match means no offer. The previous "first available, any
    // size" fallback offered wrong-size items (e.g. a Full protector on a King
    // mattress when King variants were OOS) and is intentionally removed.
    if (candidates.length === 0) return null;

    var best = [];
    for (var b = 0; b < candidates.length; b++) {
      if (candidates[b].score === bestScore) best.push(candidates[b].variant);
    }

    // Pass 2 — depth filter, only when a required depth was supplied.
    if (requiredDepthIn === null) {
      return best[0]; // legacy behavior: first best size match in variant order
    }

    var exact = null;
    var neutral = null;
    var deeper = null;
    var deeperDepth = null;
    for (var j = 0; j < best.length; j++) {
      var cv = best[j];
      var cvDepth = variantDepthInches(cv);
      if (cvDepth === null) {
        if (!neutral) neutral = cv;
      } else if (cvDepth === requiredDepthIn) {
        if (!exact) exact = cv;
      } else if (cvDepth > requiredDepthIn) {
        if (!deeper || cvDepth < deeperDepth) {
          deeper = cv;
          deeperDepth = cvDepth;
        }
      }
    }
    if (exact) return exact;
    if (neutral) return neutral; // candidate carries no depth token → size-only product
    if (allowDeeperDepthFallback && deeper) return deeper;
    return null;
  }

  return {
    DEFAULT_DEPTH_THRESHOLD_IN: DEFAULT_DEPTH_THRESHOLD_IN,
    normalizeSizeName: normalizeSizeName,
    extractDepthInches: extractDepthInches,
    variantDepthInches: variantDepthInches,
    isSingleUniversalAddon: isSingleUniversalAddon,
    requiredDepthFor: requiredDepthFor,
    findMatchingVariant: findMatchingVariant,
  };
})();
